import {
  Box,
  Divider,
  Grid,
  LinearProgress,
  Portal,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ComparisonPill from "Components/ComparisonPill/ComparisonPill";
import GlassCard from "Components/GlassCard/GlassCard";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import {
  CampaignsDropdown,
  ChartLegendItem,
  useChartData,
} from "features/analytics";
import SelectEmptyState from "features/common/components/SelectEmptyState";
import { ScienceOutlined } from "@mui/icons-material";
import {
  ApiCampaignType,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiProjectType,
  ApiYAxisChartType,
} from "@incendium/api";
import Loading from "Components/Loading/Loading";
import { AnimatePresence, motion } from "motion/react";
import { avgArray, formatNumber, sumArray } from "Helpers/numbers";
import { StyledMiddleBox } from "Components/UI/StylesFlexBox";
import CircularProgressBar from "Components/CircularProgressBar/CircularProgressBar";
import { useCampaigns } from "Hooks/useCampaigns";
import { percentageOf } from "Helpers/percentage";
import moment from "moment";
import { friendlyDate } from "Helpers/dates";
import { useSearchParam } from "react-use";
import { TChartData } from "Interfaces";
import { cumulativeSumArray } from "Helpers/arrays";
import { AnalyticsSpacing, PageActionPreSlot } from "consts";
import { getDataValueOrZeroByKey } from "Helpers/analytics";
import { useHasProjectType } from "features/project";

const VARIANT = "Variant";
const CONTROL = "Control";

function CampaignABOverviewPage() {
  const { selectedProject } = useSelectedProject();
  const hasProjectType = useHasProjectType();
  const theme = useTheme();
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const { chartOutput } = useFromToContext();
  const [avgVariantMacroConversions, setAvgVariantMacroConversions] =
    useState(0);
  const [avgControlMacroConversions, setAvgControlMacroConversions] =
    useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [lastFromStartChartData, setLastFromStartChartData] = useState<
    TChartData[]
  >([]);
  const [
    avgVariantImpactedMacroConversions,
    setAvgVariantImpactedMacroConversions,
  ] = useState(0);
  const [
    avgControlImpactedMacroConversions,
    setAvgControlImpactedMacroConversions,
  ] = useState(0);
  const { campaigns } = useCampaigns();
  const campaignParam = useSearchParam("campaign");

  // Sync URL campaign param to selection when campaigns load
  useEffect(() => {
    if (!campaigns?.length || !campaignParam) return;
    const hasCampaign = campaigns.find((c) => c.id === Number(campaignParam));
    if (hasCampaign?.name) {
      setSelectedCampaign(hasCampaign.name);
    }
  }, [campaigns, campaignParam]);

  const campaignChart = useMemo(
    () => ({
      name: "",
      dimension: [
        ApiDimension.DIMENSION_INTERNAL_AB_CAMPAIGN,
        ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS,
      ],
      yAxisKeys: [
        {
          fields: [
            ApiMetric.METRIC_PAGEVIEWS_COUNT,
            ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
            ApiMetric.METRIC_LEADS_COUNT,
            ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
            ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
            ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
            ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE,
            ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE,
            ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
            ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED,
            ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
            ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
            ApiMetric.METRIC_LANDING_PAGE_SESSION_MACRO_CONVERSION_RATE,
          ],
        },
      ],
      includeSubdomains: true,
      attributes: [],
    }),
    []
  );

  const { chartData: keyData, loading } = useChartData(
    selectedProject,
    campaignChart,
    chartOutput
  );

  const selectedControlChartData = useMemo(() => {
    if (!keyData) {
      return null;
    }
    return (keyData.data || []).find(
      (row) =>
        row.name === selectedCampaign &&
        row[ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS] === CONTROL
    );
  }, [keyData, selectedCampaign]);

  const selectedVariantChartData = useMemo(() => {
    if (!keyData) {
      return null;
    }
    return (keyData.data || []).find(
      (row) =>
        row.name === selectedCampaign &&
        row[ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS] === VARIANT
    );
  }, [keyData, selectedCampaign]);

  const chartAttributes = useCallback(
    (t?: string) => {
      const arr: ApiChartAttribute[] = [];
      if (selectedCampaign) {
        arr.push({
          key: ApiDimension.DIMENSION_INTERNAL_AB_CAMPAIGN,
          value: selectedCampaign,
        });
      }
      if (t) {
        arr.push({
          key: ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS,
          value: t,
        });
      }
      return arr;
    },
    [selectedCampaign]
  );

  const keyStats = useMemo(() => {
    return [
      {
        name: "Pageviews",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_PAGEVIEWS_COUNT
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_PAGEVIEWS_COUNT
        ),
        isPercentage: false,
        metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
      },
      {
        name: "Effective Pageview Rate",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE
        ),
        isPercentage: true,
        metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
      },
      {
        name: "Average Time on Page",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE
        ),
        isPercentage: false,
        metric: ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
      },
      {
        name: "Average Viewed Percentage",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE
        ),
        isPercentage: true,
        metric: ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE,
      },
      {
        name: "Average Scrolled Percentage",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE
        ),
        isPercentage: true,
        metric: ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE,
      },

      {
        name: "Interactions",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT
        ),
        isPercentage: false,
        metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
      },
      {
        name: "Interactions Rate",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE
        ),
        isPercentage: true,
        metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
      },
      {
        name: "Impacted Conversions",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED
        ),
        isPercentage: false,
        metric:
          ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
      },
      {
        name: "Impacted Conversion Rate",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED
        ),
        isPercentage: true,
        metric:
          ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED,
      },
      {
        name: "Session Conversions",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST
        ),
        isPercentage: false,
        metric: ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
      },
      {
        name: "Conversion Rate",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST
        ),
        isPercentage: true,
        metric: ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
      },
      {
        name: "Landing Page Session Conversion Rate",
        current: getDataValueOrZeroByKey(
          selectedVariantChartData,
          ApiMetric.METRIC_LANDING_PAGE_SESSION_MACRO_CONVERSION_RATE
        ),
        comparison: getDataValueOrZeroByKey(
          selectedControlChartData,
          ApiMetric.METRIC_LANDING_PAGE_SESSION_MACRO_CONVERSION_RATE
        ),
        isPercentage: true,
        metric: ApiMetric.METRIC_LANDING_PAGE_SESSION_MACRO_CONVERSION_RATE,
      },
    ];
  }, [selectedControlChartData, selectedVariantChartData]);

  const apiCampaign = useMemo(() => {
    return (campaigns || []).find((c) => c.name === selectedCampaign);
  }, [campaigns, selectedCampaign]);

  const fromStartChartDate = useMemo(
    () =>
      apiCampaign
        ? {
          from: apiCampaign?.startDate || apiCampaign?.createdAt,
          to: new Date(),
          lastNDays: undefined,
        }
        : null,
    [apiCampaign]
  );

  const estimatedEndDate = useMemo(() => {
    if (!lastFromStartChartData || !apiCampaign?.maxNumberOfLeads) {
      return null;
    }
    // get per day
    const total = sumArray(
      (lastFromStartChartData || []).map((d) =>
        Number(d[ApiMetric.METRIC_LEADS_COUNT])
      )
    );
    // work out what percentage we have been doing per day
    const numberOfDays = (lastFromStartChartData || []).length;
    const complete = percentageOf(total, apiCampaign.maxNumberOfLeads);
    const daysNeeded = 100 / (complete / numberOfDays);
    return friendlyDate(moment().add(daysNeeded, "days"));
  }, [lastFromStartChartData, apiCampaign]);

  const onCampaignLifespanChange = useCallback((data) => {
    setLastFromStartChartData(data);
    setTotalLeads(
      sumArray(
        data.map((d) => Number(d[ApiMetric.METRIC_NEW_CAMPAIGN_LEAD_COUNT]))
      )
    );
  }, []);

  const tableMetrics = useMemo(() => {
    const arr = [
      ApiMetric.METRIC_PAGEVIEWS_COUNT,
      ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
      ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
      ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED,
      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
      ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
    ];
    if (hasProjectType(ApiProjectType.TYPE_ECOMMERCE) || true) {
      arr.push(
        ...[
          ApiMetric.METRIC_REVENUE_IMPACTED,
          ApiMetric.METRIC_SALES_COUNT_IMPACTED,
          ApiMetric.METRIC_AVERAGE_SALE_VALUE,
        ]
      );
    }
    return arr;
  }, [hasProjectType]);

  if (!selectedCampaign) {
    return (
      <SelectEmptyState
        icon={
          <ScienceOutlined
            sx={{ fontSize: 40, color: "primary.main", opacity: 0.9 }}
          />
        }
        title="Select a campaign to view A/B test insights"
        dropdown={
          <CampaignsDropdown
            sx={{ width: "100%", maxWidth: 320 }}
            label="Campaign"
            variant="outlined"
            value={selectedCampaign}
            onChange={(name) => setSelectedCampaign(name)}
            campaignTypes={ApiCampaignType.CAMPAIGN_TYPE_AB}
          />
        }
        table={
          <AnalyticsCard
            onClick={{
              [ApiDimension.DIMENSION_INTERNAL_AB_CAMPAIGN]: [
                {
                  text: "Select Campaign",
                  fn: (p) => setSelectedCampaign(p || ""),
                },
              ],
            }}
            chart={{
              name: "Campaign A/B Insights",
              dimension: [ApiDimension.DIMENSION_INTERNAL_AB_CAMPAIGN],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                    ApiMetric.METRIC_LEADS_COUNT,
                    ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
                    ApiMetric.METRIC_MACRO_CONVERSION_RATE_IMPACTED,
                    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
                    ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST,
                  ],
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                rowsPerPage: 0,
                noTitle: true,
              },
            }}
          />
        }
      />
    );
  }

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <Loading text="Fetching Campaign Data" />
      ) : (
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Portal container={() => document.getElementById(PageActionPreSlot)}>
            <CampaignsDropdown
              sx={{ width: 270 }}
              label="Campaign"
              variant="outlined"
              size="small"
              value={selectedCampaign}
              onChange={(name) => setSelectedCampaign(name)}
              campaignTypes={ApiCampaignType.CAMPAIGN_TYPE_AB}
            />
          </Portal>
          <Grid container spacing={AnalyticsSpacing} mb={4}>
            <Grid item xs={12} container spacing={AnalyticsSpacing}>
              <Grid item xs={3} sx={{ height: 1080 }}>
                <GlassCard
                  noShadow
                  sx={{ height: "100%" }}
                  boxProps={{
                    px: 3,
                    py: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                    Key Statistics
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ lineHeight: 1.2 }}
                    color={"secondary"}
                    fontWeight={500}
                    mb={2}
                  >
                    See how the variant performed vs. control
                  </Typography>
                  <Divider />
                  <Box>
                    <Stack
                      my={2}
                      direction="row"
                      spacing={0.5}
                      justifyContent="space-between"
                    >
                      <ChartLegendItem item="Variant" index={1} />
                      <ChartLegendItem item="Control" index={3} />
                    </Stack>
                    {(keyStats || []).map((stat) => (
                      <ComparisonPill
                        key={stat.name}
                        title={stat.name}
                        value={stat.current}
                        comparisonValue={stat.comparison}
                        isPercentage={stat.isPercentage}
                        metric={stat.metric}
                      />
                    ))}
                  </Box>
                </GlassCard>
              </Grid>

              <Grid item xs={5.5} container spacing={AnalyticsSpacing}>
                <Grid item xs={6} sx={{ height: 300 }}>
                  <AnalyticsCard
                    chart={{
                      name: "Variant - Top 5 Traffic Sources",
                      description: "Based on Pageviews",
                      dimension: [ApiDimension.DIMENSION_CHANNEL],
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.PIE,
                      limit: 5,
                      attributes: chartAttributes(VARIANT),
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                        },
                      ],
                      displayOptions: {
                        noLegend: true,
                        noFilters: true,
                        showTotals: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} sx={{ height: 300 }}>
                  <AnalyticsCard
                    chart={{
                      name: "Control - Top 5 Traffic Sources",
                      description: "Based on Pageviews",
                      dimension: [ApiDimension.DIMENSION_CHANNEL],
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.PIE,
                      limit: 5,
                      attributes: chartAttributes(CONTROL),
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                        },
                      ],
                      displayOptions: {
                        noLegend: true,
                        noFilters: true,
                        showTotals: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ height: 390 }}>
                  <AnalyticsCard
                    chart={{
                      name: "Variant vs. Control Pageviews",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                        ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS,
                      ],
                      orderBy: `${ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS}`,
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: chartAttributes(),
                      includeSubdomains: true,
                      yAxisKeys: [
                        {
                          key: "l",

                          fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT],
                        },
                      ],
                      displayOptions: {
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ height: 390 }}>
                  <AnalyticsCard
                    chart={{
                      name: "Variant vs. Control Effective Pageview Rate",
                      dimension: [
                        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                        ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS,
                      ],
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: chartAttributes(),
                      includeSubdomains: true,
                      orderBy: `${ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS}`,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE],
                        },
                      ],
                      displayOptions: {
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item xs={3.5} container spacing={AnalyticsSpacing}>
                <Grid item xs={12} sx={{ height: 455 }}>
                  <GlassCard
                    noShadow
                    boxProps={{
                      px: 2,
                      py: 1.5,
                    }}
                    sx={{
                      overflow: "hidden",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Stack sx={{ height: "100%" }}>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          mb={2}
                          sx={{ lineHeight: 1.2 }}
                        >
                          Leads Shown the AB Test {totalLeads} /{" "}
                          {apiCampaign?.maxNumberOfLeads || "N/A"}
                        </Typography>
                        <LinearProgress
                          value={50}
                          variant="determinate"
                          color="primary"
                          sx={{
                            height: 16,
                            borderRadius: 8,
                          }}
                        />
                        {apiCampaign?.maxNumberOfLeads &&
                          totalLeads >= apiCampaign?.maxNumberOfLeads ? (
                          <Typography
                            variant="subtitle2"
                            color={"secondary"}
                            mt={1}
                          >
                            Campaign Complete
                          </Typography>
                        ) : estimatedEndDate ? (
                          <Typography
                            variant="subtitle2"
                            color={"secondary"}
                            mt={1}
                          >
                            Estimated completion date: {estimatedEndDate}
                          </Typography>
                        ) : (
                          <></>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {apiCampaign && fromStartChartDate && (
                          <AnalyticsCard
                            noborder
                            boxProps={{
                              paddingTop: 0,
                              paddingLeft: 0,
                              paddingRight: 0,
                            }}
                            customDataFn={(data) => {
                              return cumulativeSumArray(
                                data,
                                ApiMetric.METRIC_NEW_CAMPAIGN_LEAD_COUNT
                              );
                            }}
                            overrideDate={fromStartChartDate}
                            chart={{
                              name: "",
                              dimension: [
                                ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                              ],
                              template: ApiChartTemplate.TREND,
                              type: ApiChartType.GRAPH,
                              attributes: chartAttributes(),
                              includeSubdomains: true,
                              yAxisKeys: [
                                {
                                  key: "l",
                                  fields: [
                                    ApiMetric.METRIC_NEW_CAMPAIGN_LEAD_COUNT,
                                  ],
                                  chart: {
                                    [ApiMetric.METRIC_NEW_CAMPAIGN_LEAD_COUNT]:
                                      ApiYAxisChartType.AREA,
                                  },
                                },
                              ],
                              displayOptions: {
                                noFilters: true,
                                noLegend: true,
                                noTitle: true,
                              },
                            }}
                            onDataChange={onCampaignLifespanChange}
                          />
                        )}
                      </Box>
                    </Stack>
                  </GlassCard>
                </Grid>

                <Grid item xs={12} sx={{ height: 625 }}>
                  <GlassCard
                    noShadow
                    sx={{ height: "100%" }}
                    boxProps={{
                      px: 3,
                      py: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ lineHeight: 1.2 }}
                      mb={5}
                    >
                      Variant vs. Control Conversions
                    </Typography>
                    <Grid container spacing={5}>
                      <Grid item xs={6}>
                        <StyledMiddleBox flexDirection={"column"} mb={1}>
                          <Typography variant="subtitle2">Variant</Typography>
                          <Box sx={{ height: 150, width: "100%" }}>
                            <AnalyticsCard
                              noborder
                              noShadow
                              noToolbar
                              boxProps={{
                                padding: 0,
                              }}
                              chart={{
                                name: "",
                                dimension: [
                                  ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                                ],
                                template: ApiChartTemplate.TREND,
                                type: ApiChartType.GRAPH,
                                attributes: chartAttributes(VARIANT),
                                includeSubdomains: true,
                                yAxisKeys: [
                                  {
                                    key: "l",
                                    fields: [
                                      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
                                    ],
                                  },
                                ],
                                displayOptions: {
                                  noFilters: true,
                                  noLegend: true,
                                  noTitle: true,
                                  noAxis: true,
                                },
                              }}
                              onDataChange={(data) => {
                                setAvgVariantMacroConversions(
                                  avgArray(
                                    data
                                      .map((d) =>
                                        Number(
                                          d[
                                          ApiMetric
                                            .METRIC_MACRO_CONVERSIONS_COUNT_LAST
                                          ]
                                        )
                                      )
                                      .filter((d) => d > 0)
                                  )
                                );
                              }}
                            />
                          </Box>
                          <Typography variant="body2" textAlign={"center"}>
                            {formatNumber(avgVariantMacroConversions)} avg.
                            daily session conversions
                          </Typography>
                        </StyledMiddleBox>
                      </Grid>
                      <Grid item xs={6}>
                        <StyledMiddleBox flexDirection={"column"} mb={1}>
                          <Typography variant="subtitle2">Control</Typography>
                          <Box sx={{ height: 150, width: "100%" }}>
                            <AnalyticsCard
                              noborder
                              noShadow
                              noToolbar
                              boxProps={{
                                padding: 0,
                              }}
                              chart={{
                                name: "",
                                dimension: [
                                  ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                                ],
                                template: ApiChartTemplate.TREND,
                                type: ApiChartType.GRAPH,
                                attributes: chartAttributes(CONTROL),
                                includeSubdomains: true,
                                yAxisKeys: [
                                  {
                                    key: "l",
                                    fields: [
                                      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_LAST,
                                    ],
                                  },
                                ],
                                displayOptions: {
                                  noFilters: true,
                                  noLegend: true,
                                  noTitle: true,
                                  noAxis: true,
                                },
                              }}
                              onDataChange={(data) => {
                                setAvgControlMacroConversions(
                                  avgArray(
                                    data
                                      .map((d) =>
                                        Number(
                                          d[
                                          ApiMetric
                                            .METRIC_MACRO_CONVERSIONS_COUNT_LAST
                                          ]
                                        )
                                      )
                                      .filter((d) => d > 0)
                                  )
                                );
                              }}
                            />
                          </Box>
                          <Typography variant="body2" textAlign={"center"}>
                            {formatNumber(avgControlMacroConversions)} avg.
                            daily session conversions
                          </Typography>
                        </StyledMiddleBox>
                      </Grid>
                      <Grid item xs={6}>
                        <StyledMiddleBox
                          flexDirection={"column"}
                          alignItems="center"
                          mb={1}
                        >
                          <Typography variant="subtitle2">Variant</Typography>
                          <Box sx={{ height: 150, width: "100%" }}>
                            <AnalyticsCard
                              noborder
                              noShadow
                              noToolbar
                              boxProps={{
                                padding: 0,
                              }}
                              chart={{
                                name: "",
                                dimension: [
                                  ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                                ],
                                template: ApiChartTemplate.TREND,
                                type: ApiChartType.GRAPH,
                                attributes: chartAttributes(VARIANT),
                                includeSubdomains: true,
                                yAxisKeys: [
                                  {
                                    key: "l",
                                    fields: [
                                      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
                                    ],
                                  },
                                ],
                                displayOptions: {
                                  noFilters: true,
                                  noLegend: true,
                                  noTitle: true,
                                  noAxis: true,
                                  noAttributionToggle: true,
                                },
                              }}
                              onDataChange={(data) => {
                                setAvgVariantImpactedMacroConversions(
                                  avgArray(
                                    data
                                      .map((d) =>
                                        Number(
                                          d[
                                          ApiMetric
                                            .METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED
                                          ]
                                        )
                                      )
                                      .filter((d) => d > 0)
                                  )
                                );
                              }}
                            />
                          </Box>
                          <Typography variant="body2" textAlign={"center"}>
                            {formatNumber(avgVariantImpactedMacroConversions)}{" "}
                            avg. daily impacted Conversions
                          </Typography>
                        </StyledMiddleBox>
                      </Grid>
                      <Grid item xs={6}>
                        <StyledMiddleBox flexDirection={"column"} mb={1}>
                          <Typography variant="subtitle2">Control</Typography>
                          <Box sx={{ height: 150, width: "100%" }}>
                            <AnalyticsCard
                              noborder
                              noShadow
                              noToolbar
                              boxProps={{
                                padding: 0,
                              }}
                              chart={{
                                name: "",
                                dimension: [
                                  ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                                ],
                                template: ApiChartTemplate.TREND,
                                type: ApiChartType.GRAPH,
                                attributes: chartAttributes(CONTROL),
                                includeSubdomains: true,
                                yAxisKeys: [
                                  {
                                    key: "l",
                                    fields: [
                                      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED,
                                    ],
                                  },
                                ],
                                displayOptions: {
                                  noFilters: true,
                                  noLegend: true,
                                  noTitle: true,
                                  noAxis: true,
                                  noAttributionToggle: true,
                                },
                              }}
                              onDataChange={(data) => {
                                setAvgControlImpactedMacroConversions(
                                  avgArray(
                                    data
                                      .map((d) =>
                                        Number(
                                          d[
                                          ApiMetric
                                            .METRIC_MACRO_CONVERSIONS_COUNT_IMPACTED
                                          ]
                                        )
                                      )
                                      .filter((d) => d > 0)
                                  )
                                );
                              }}
                            />
                          </Box>
                          <Typography variant="body2" textAlign={"center"}>
                            {formatNumber(avgControlImpactedMacroConversions)}{" "}
                            avg. daily impacted Conversions
                          </Typography>
                        </StyledMiddleBox>
                      </Grid>
                    </Grid>
                  </GlassCard>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={4} sx={{ height: 320 }}>
              <GlassCard
                noShadow
                sx={{ height: "100%" }}
                boxProps={{
                  px: 3,
                  py: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }} mb={4}>
                  Variant vs. Control Effective Pageview Rate
                </Typography>
                <Stack direction="row" justifyContent="space-around">
                  <StyledMiddleBox flexDirection={"column"}>
                    <Typography gutterBottom variant="subtitle2">
                      Variant
                    </Typography>
                    <CircularProgressBar
                      value={Number(
                        selectedVariantChartData
                          ? selectedVariantChartData[
                          ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE
                          ]
                          : 0
                      )}
                      customColour={theme.palette.charts[4]}
                    />
                    <Typography mt={1} align="center" variant="body2">
                      Effective Pageview Rate
                    </Typography>
                  </StyledMiddleBox>
                  <StyledMiddleBox flexDirection={"column"}>
                    <Typography gutterBottom variant="subtitle2">
                      Control
                    </Typography>
                    <CircularProgressBar
                      value={Number(
                        selectedControlChartData
                          ? selectedControlChartData[
                          ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE
                          ]
                          : 0
                      )}
                      customColour={theme.palette.charts[2]}
                    />
                    <Typography mt={1} align="center" variant="body2">
                      Effective Pageview Rate
                    </Typography>
                  </StyledMiddleBox>
                </Stack>
              </GlassCard>
            </Grid>
            <Grid item xs={4} sx={{ height: 320 }}>
              <GlassCard
                noShadow
                sx={{ height: "100%" }}
                boxProps={{
                  px: 3,
                  py: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }} mb={4}>
                  Variant vs. Control Conversion Rate
                </Typography>
                <Stack direction="row" justifyContent="space-around">
                  <StyledMiddleBox flexDirection={"column"}>
                    <Typography gutterBottom variant="subtitle2">
                      Variant
                    </Typography>
                    <CircularProgressBar
                      value={Number(
                        selectedVariantChartData
                          ? selectedVariantChartData[
                          ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST
                          ]
                          : 0
                      )}
                      customColour={theme.palette.charts[4]}
                    />
                    <Typography mt={1} align="center" variant="body2">
                      Conversion Rate
                    </Typography>
                  </StyledMiddleBox>
                  <StyledMiddleBox flexDirection={"column"}>
                    <Typography gutterBottom variant="subtitle2">
                      Control
                    </Typography>
                    <CircularProgressBar
                      value={Number(
                        selectedControlChartData
                          ? selectedControlChartData[
                          ApiMetric.METRIC_MACRO_CONVERSION_RATE_LAST
                          ]
                          : 0
                      )}
                      customColour={theme.palette.charts[2]}
                    />
                    <Typography mt={1} align="center" variant="body2">
                      Conversion Rate
                    </Typography>
                  </StyledMiddleBox>
                </Stack>
              </GlassCard>
            </Grid>
            <Grid item xs={4} sx={{ height: 320 }}>
              <GlassCard
                noShadow
                sx={{ height: "100%" }}
                boxProps={{
                  px: 3,
                  py: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }} mb={4}>
                  Variant vs. Control Impacted Conversion Rate
                </Typography>
                <Stack direction="row" justifyContent="space-around">
                  <StyledMiddleBox flexDirection={"column"}>
                    <Typography gutterBottom variant="subtitle2">
                      Variant
                    </Typography>
                    <CircularProgressBar
                      value={Number(
                        selectedVariantChartData
                          ? selectedVariantChartData[
                          ApiMetric
                            .METRIC_MACRO_CONVERSION_RATE_IMPACTED
                          ]
                          : 0
                      )}
                      customColour={theme.palette.charts[4]}
                    />
                    <Typography mt={1} align="center" variant="body2">
                      Impacted Conversion
                      <br /> Conversion Rate
                    </Typography>
                  </StyledMiddleBox>
                  <StyledMiddleBox flexDirection={"column"}>
                    <Typography gutterBottom variant="subtitle2">
                      Control
                    </Typography>
                    <CircularProgressBar
                      value={Number(
                        selectedControlChartData
                          ? selectedControlChartData[
                          ApiMetric
                            .METRIC_MACRO_CONVERSION_RATE_IMPACTED
                          ]
                          : 0
                      )}
                      customColour={theme.palette.charts[2]}
                    />
                    <Typography mt={1} align="center" variant="body2">
                      Impacted Conversion
                      <br /> Conversion Rate
                    </Typography>
                  </StyledMiddleBox>
                </Stack>
              </GlassCard>
            </Grid>

            <Grid item xs={6} sx={{ minHeight: 350 }}>
              <AnalyticsCard
                chart={{
                  name: "Variant - Device Performance",
                  dimension: [ApiDimension.DIMENSION_DEVICE],
                  template: ApiChartTemplate.TABLE,
                  type: ApiChartType.TABLE,
                  attributes: chartAttributes(VARIANT),
                  includeSubdomains: true,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: tableMetrics,
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                    noAttributionToggle: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} sx={{ minHeight: 350 }}>
              <AnalyticsCard
                chart={{
                  name: "Control - Device Performance",
                  dimension: [ApiDimension.DIMENSION_DEVICE],
                  template: ApiChartTemplate.TABLE,
                  type: ApiChartType.TABLE,
                  attributes: chartAttributes(CONTROL),
                  includeSubdomains: true,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: tableMetrics,
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                    noAttributionToggle: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} sx={{ minHeight: 470 }}>
              <AnalyticsCard
                chart={{
                  name: "Variant - Browser Performance",
                  dimension: [ApiDimension.DIMENSION_BROWSER],
                  template: ApiChartTemplate.TABLE,
                  type: ApiChartType.TABLE,
                  attributes: chartAttributes(VARIANT),
                  includeSubdomains: true,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: tableMetrics,
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                    noAttributionToggle: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} sx={{ minHeight: 470 }}>
              <AnalyticsCard
                chart={{
                  name: "Control - Browser Performance",
                  dimension: [ApiDimension.DIMENSION_BROWSER],
                  template: ApiChartTemplate.TABLE,
                  type: ApiChartType.TABLE,
                  attributes: chartAttributes(CONTROL),
                  includeSubdomains: true,
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: tableMetrics,
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                    noAttributionToggle: true,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </AnimatePresence>
  );
}

export default CampaignABOverviewPage;
